import { setToStorage, getFromStorage } from '../utils/localStorage';
import { formatDateEST } from '../utils/dateHelpers';

const FLAG_SOURCES = {
  // Facebook scraping via CORS proxy
  facebook: {
    url: 'https://api.allorigins.win/get?url=' + encodeURIComponent('https://www.facebook.com/sgcfire/'),
    name: 'South Gulf County Fire Facebook',
    parser: async (response) => {
      try {
        const html = response.contents || '';
        
        // Look for recent posts with flag information
        // Facebook posts typically contain date patterns like "June 22" or "6/22"
        const today = new Date();
        const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
        const currentMonth = monthNames[today.getMonth()];
        const currentDay = today.getDate();
        
        // Pattern to find flag posts for today or recent days
        const datePattern = new RegExp(`(${currentMonth}\\s+${currentDay}|${today.getMonth() + 1}/${currentDay}).*?(north|North).*?(flags?|Flags?)`, 'i');
        const flagPattern = /(?:double\s+red|red|yellow|green|purple)\s+flag/gi;
        
        // Find text that matches date and mentions north flags
        const matches = html.match(datePattern);
        if (matches) {
          // Look for flag color in nearby text
          const contextStart = html.indexOf(matches[0]);
          const contextText = html.substring(contextStart, contextStart + 500);
          
          const flagMatches = contextText.match(flagPattern);
          if (flagMatches && flagMatches.length > 0) {
            const flagText = flagMatches[0].toLowerCase();
            
            if (flagText.includes('double red')) {
              return {
                color: '🔴🔴',
                text: 'Double Red - Water closed',
                details: 'Water closed to public - North St. Joseph Peninsula',
                source: 'South Gulf Fire Facebook'
              };
            } else if (flagText.includes('red')) {
              return {
                color: '🔴',
                text: 'Red - High hazard',
                details: 'High surf and/or strong currents - North St. Joseph Peninsula',
                source: 'South Gulf Fire Facebook'
              };
            } else if (flagText.includes('yellow')) {
              return {
                color: '🟡',
                text: 'Yellow - Medium hazard',
                details: 'Moderate surf and/or currents - North St. Joseph Peninsula',
                source: 'South Gulf Fire Facebook'
              };
            } else if (flagText.includes('green')) {
              return {
                color: '🟢',
                text: 'Green - Low hazard',
                details: 'Calm conditions - North St. Joseph Peninsula',
                source: 'South Gulf Fire Facebook'
              };
            } else if (flagText.includes('purple')) {
              return {
                color: '🟣',
                text: 'Purple - Marine pests',
                details: 'Dangerous marine life present - North St. Joseph Peninsula',
                source: 'South Gulf Fire Facebook'
              };
            }
          }
        }
      } catch (error) {
        console.error('Error parsing Facebook content:', error);
      }
      return null;
    }
  },
  // NOAA Weather Hazards - Backup source
  noaaHazards: {
    url: 'https://api.weather.gov/alerts/active?zone=FLZ114', // Gulf County zone
    name: 'NOAA Weather Service',
    parser: (data) => {
      // Look for beach hazard statements
      const hazards = data.features || [];
      for (const hazard of hazards) {
        const headline = hazard.properties?.headline || '';
        const description = hazard.properties?.description || '';
        const event = hazard.properties?.event || '';
        
        // Check for rip current or surf advisories
        if (headline.toLowerCase().includes('high rip current') || 
            description.toLowerCase().includes('dangerous rip currents') ||
            event.toLowerCase().includes('high rip current')) {
          return {
            color: '🔴',
            text: 'Red - High hazard',
            details: 'High rip current risk - ' + headline,
            source: 'NOAA Weather Advisory'
          };
        }
        
        if (headline.toLowerCase().includes('moderate rip current') || 
            description.toLowerCase().includes('moderate risk')) {
          return {
            color: '🟡',
            text: 'Yellow - Medium hazard',
            details: 'Moderate rip current risk',
            source: 'NOAA Weather Advisory'
          };
        }
      }
      return null;
    }
  }
};

class FlagService {
  constructor() {
    this.cacheKey = 'beach_flag_data';
    this.cacheTimeout = 30 * 60 * 1000; // 30 minutes
  }

  async fetchCurrentFlags() {
    // Check cache first
    const cached = this.getCachedFlags();
    if (cached && !this.isCacheExpired(cached)) {
      return cached.data;
    }

    // Try each source
    for (const [sourceId, source] of Object.entries(FLAG_SOURCES)) {
      try {
        const response = await fetch(source.url);
        if (response.ok) {
          const data = await response.json();
          // Handle async parsers (like Facebook)
          const flagData = await Promise.resolve(source.parser(data));
          
          if (flagData) {
            const result = {
              ...flagData,
              lastUpdated: formatDateEST(new Date()),
              source: flagData.source || source.name,
              lastChecked: Date.now()
            };
            
            this.cacheFlags(result);
            return result;
          }
        }
      } catch (error) {
        console.error(`Failed to fetch from ${source.name}:`, error);
      }
    }


    // Return cached data even if expired, or default
    return cached?.data || this.getDefaultFlag();
  }

  getCachedFlags() {
    return getFromStorage(this.cacheKey);
  }

  cacheFlags(data) {
    setToStorage(this.cacheKey, {
      data,
      timestamp: Date.now()
    });
  }

  isCacheExpired(cached) {
    return Date.now() - cached.timestamp > this.cacheTimeout;
  }

  getDefaultFlag() {
    return {
      color: '🟡',
      text: 'Yellow - Medium hazard (Default)',
      details: 'Unable to fetch current flags. Check South Gulf Fire Rescue Facebook for North St. Joseph Peninsula flags.',
      lastUpdated: formatDateEST(new Date()),
      source: 'Default - Check Facebook for current flags',
      lastChecked: Date.now()
    };
  }

  // Parse text that might contain flag information
  parseFlagFromText(text) {
    const lowerText = text.toLowerCase();
    
    // Check for specific flag mentions
    if (lowerText.includes('double red') || lowerText.includes('water closed')) {
      return {
        color: '🔴🔴',
        text: 'Double Red - Water closed',
        details: 'Water closed to public'
      };
    }
    
    if (lowerText.includes('red flag')) {
      return {
        color: '🔴',
        text: 'Red - High hazard',
        details: 'High surf and/or strong currents'
      };
    }
    
    if (lowerText.includes('yellow flag')) {
      return {
        color: '🟡',
        text: 'Yellow - Medium hazard',
        details: 'Moderate surf and/or currents'
      };
    }
    
    if (lowerText.includes('green flag')) {
      return {
        color: '🟢',
        text: 'Green - Low hazard',
        details: 'Calm conditions'
      };
    }
    
    if (lowerText.includes('purple flag')) {
      return {
        color: '🟣',
        text: 'Purple - Marine pests',
        details: 'Dangerous marine life present'
      };
    }
    
    return null;
  }
}

export default new FlagService();